import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import FileUpload, { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Disc, Star, Crown, X, GripVertical, ChevronDown, ChevronUp } from "lucide-react";

const ALBUM_TYPES = [
  { value: "ep", label: "EP" },
  { value: "album", label: "Album" },
];
const EDITIONS = [
  { value: "standard", label: "Standard" },
  { value: "deluxe", label: "Deluxe" },
  { value: "collector", label: "Collector" },
];

const empty = {
  title: "",
  artist_name: "Luxifer 6",
  album_type: "ep",
  edition_type: "standard",
  cover_art: "",
  additional_artwork: [],
  description: "",
  release_date: "",
  release_year: "",
  genre: "",
  track_ids: [],
  edition_extras: [],
  digital_booklet: "",
  price: 4.99,
  sale_price: 0,
  is_free: false,
  is_visible: true,
  featured: false,
  exclusive: false,
};

const Field = ({ label, hint, children, className = "" }) => (
  <div className={className}>
    <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">
      {label} {hint && <span className="text-white/20 normal-case tracking-normal">{hint}</span>}
    </label>
    {children}
  </div>
);
const Text = (p) => (<input {...p} className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 ${p.className||""}`} />);
const Area = (p) => (<textarea {...p} className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 resize-none ${p.className||""}`} />);
const Sel = ({children, ...p}) => (<select {...p} className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] ${p.className||""}`}>{children}</select>);

export default function AlbumsManager() {
  const [items, setItems] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExtras, setShowExtras] = useState(false);

  const fetch_ = async () => {
    const [a, m] = await Promise.all([
      api.get("/albums?admin=true"),
      api.get("/music?admin=true"),
    ]);
    setItems(a.data);
    setTracks(m.data);
    setLoading(false);
  };
  useEffect(() => { fetch_().catch(() => setLoading(false)); }, []);

  const openAdd = () => { setEditId(null); setForm({...empty}); setShowExtras(false); setOpen(true); };
  const openEdit = (item) => {
    setEditId(item.id);
    setForm({ ...empty, ...item, track_ids: item.track_ids || [], edition_extras: item.edition_extras || [] });
    setShowExtras(false);
    setOpen(true);
  };
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.track_ids.length === 0) return toast.error("Add at least one track");
    const payload = { ...form };
    if (!payload.release_year && payload.release_date) payload.release_year = String(payload.release_date).slice(0,4);
    if (payload.is_free) payload.price = 0;
    try {
      if (editId) await api.put(`/albums/${editId}`, payload);
      else await api.post("/albums", payload);
      toast.success(editId ? "Album updated" : "Album added");
      setOpen(false); fetch_();
    } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this album?")) return;
    try { await api.delete(`/albums/${id}`); toast.success("Deleted"); fetch_(); }
    catch { toast.error("Failed"); }
  };

  const setField = (k,v) => setForm(f => ({...f, [k]: v}));
  const addTrack = (id) => setField("track_ids", [...form.track_ids, id]);
  const removeTrack = (id) => setField("track_ids", form.track_ids.filter(t => t !== id));
  const moveTrack = (idx, dir) => {
    const ids = [...form.track_ids];
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    setField("track_ids", ids);
  };
  const addExtra = () => setField("edition_extras", [...(form.edition_extras||[]), { title:"", file:"", note:"" }]);
  const updateExtra = (i, k, v) => {
    const arr = [...(form.edition_extras||[])];
    arr[i] = { ...arr[i], [k]: v };
    setField("edition_extras", arr);
  };
  const removeExtra = (i) => setField("edition_extras", form.edition_extras.filter((_, idx) => idx !== i));

  const tmap = Object.fromEntries(tracks.map(t => [t.id, t]));
  const availableTracks = tracks.filter(t => !form.track_ids.includes(t.id));

  const priceLabel = (item) => {
    if (item.is_free || item.price === 0) return "Free";
    if (item.sale_price > 0 && item.sale_price < item.price) return `$${item.sale_price.toFixed(2)}`;
    return `$${(item.price || 0).toFixed(2)}`;
  };

  return (
    <div data-testid="admin-albums-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">ALBUMS & EPs</h1>
          <p className="text-white/40 text-sm">{items.length} releases · bundle downloads with embedded metadata</p>
        </div>
        <Button data-testid="add-album-btn" onClick={openAdd} className="bg-[#D4AF37] text-black hover:bg-white text-sm">
          <Plus className="w-4 h-4 mr-1" /> Add Release
        </Button>
      </div>

      {loading ? <p className="text-white/30">Loading...</p> : items.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <Disc className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No albums or EPs yet.</p>
          <p className="text-xs text-white/20 mt-2">Add tracks first via the Music tab, then bundle them here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} data-testid={`album-item-${item.id}`} className={`bg-[#111111] border border-white/5 p-4 rounded flex items-center gap-4 hover:border-white/10 transition-colors ${item.is_visible === false ? "opacity-50" : ""}`}>
              {item.cover_art ? (
                <img src={getFileUrl(item.cover_art)} alt="" className="w-16 h-16 object-cover rounded flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 bg-[#1A1A1A] rounded flex items-center justify-center flex-shrink-0"><Disc className="w-6 h-6 text-white/20" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold truncate">{item.title}</p>
                  <span className="text-[9px] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-1.5 py-0.5 rounded font-bold">{(item.album_type||"ep").toUpperCase()}</span>
                  {item.edition_type && item.edition_type !== "standard" && (
                    <span className="text-[9px] uppercase text-white/80 bg-[#8B0000] px-1.5 py-0.5 rounded font-bold">{item.edition_type}</span>
                  )}
                  {item.exclusive && <span className="text-[9px] uppercase bg-[#8B0000] text-white px-1.5 py-0.5 rounded font-bold flex-shrink-0"><Crown className="w-2.5 h-2.5 inline mr-0.5" />Exclusive</span>}
                  {item.featured && <Star className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />}
                  {item.is_visible === false && <span className="text-[9px] uppercase text-white/40 border border-white/20 px-1.5 py-0.5 rounded">Hidden</span>}
                </div>
                <p className="text-xs text-white/40 truncate">
                  {item.artist_name || "Luxifer 6"}
                  {" · "}{(item.track_ids || []).length} tracks
                  {item.release_year ? ` · ${item.release_year}` : ""}
                  {item.genre ? ` · ${item.genre}` : ""}
                </p>
              </div>
              <span className="text-sm text-[#D4AF37] font-semibold hidden sm:block">{priceLabel(item)}</span>
              <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="text-white/40 hover:text-white"><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(item.id)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? "EDIT RELEASE" : "ADD RELEASE"}</DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              Bundle tracks into an EP / Album. Buyers get a ZIP with embedded metadata, cover art, and (future) booklets/stems.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title *"><Text data-testid="album-title-input" value={form.title} onChange={(e)=>setField("title",e.target.value)} placeholder="Gold Standard EP" /></Field>
              <Field label="Artist"><Text value={form.artist_name} onChange={(e)=>setField("artist_name",e.target.value)} placeholder="Luxifer 6" /></Field>
              <Field label="Type">
                <Sel value={form.album_type} onChange={(e)=>setField("album_type",e.target.value)}>
                  {ALBUM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Sel>
              </Field>
              <Field label="Edition" hint="(future-ready)">
                <Sel data-testid="album-edition-select" value={form.edition_type} onChange={(e)=>setField("edition_type",e.target.value)}>
                  {EDITIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Sel>
              </Field>
            </div>

            <Field label="Description"><Area rows={2} value={form.description} onChange={(e)=>setField("description",e.target.value)} /></Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Release Date"><Text type="date" value={form.release_date} onChange={(e)=>setField("release_date",e.target.value)} /></Field>
              <Field label="Release Year"><Text value={form.release_year} onChange={(e)=>setField("release_year",e.target.value)} placeholder="2026" /></Field>
              <Field label="Genre"><Text value={form.genre} onChange={(e)=>setField("genre",e.target.value)} placeholder="Trap" /></Field>
            </div>

            <FileUpload label="Album Cover Art (embedded in every track's ID3)" accept="image/*" value={form.cover_art} onChange={(v)=>setField("cover_art",v)} />

            {/* TRACK LIST */}
            <div className="border-t border-white/5 pt-4">
              <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Track List * <span className="text-white/20 normal-case tracking-normal">({form.track_ids.length} added)</span></label>
              <div className="space-y-2 mb-3">
                {form.track_ids.map((id, idx) => {
                  const t = tmap[id];
                  if (!t) return (
                    <div key={id} className="flex items-center gap-2 bg-[#1a0a0a] border border-red-900/40 p-2 rounded">
                      <span className="text-red-400/70 text-xs">Missing track: {id}</span>
                      <Button variant="ghost" size="icon" onClick={()=>removeTrack(id)} className="ml-auto text-white/40 hover:text-red-400 h-6 w-6"><X className="w-3 h-3" /></Button>
                    </div>
                  );
                  return (
                    <div key={id} data-testid={`album-track-${id}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/5 p-2 rounded">
                      <GripVertical className="w-3.5 h-3.5 text-white/15" />
                      <span className="text-[10px] text-white/30 font-mono w-6 text-right">{String(idx+1).padStart(2,"0")}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{t.title}</p>
                        <p className="text-[10px] text-white/30 truncate">{t.download_file ? "MP3 ✓" : <span className="text-red-400/70">No MP3 uploaded — track won't be in ZIP</span>}</p>
                      </div>
                      <div className="flex gap-0.5">
                        <Button size="icon" variant="ghost" onClick={()=>moveTrack(idx,-1)} disabled={idx===0} className="text-white/40 hover:text-white h-7 w-7 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" onClick={()=>moveTrack(idx,1)} disabled={idx===form.track_ids.length-1} className="text-white/40 hover:text-white h-7 w-7 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" onClick={()=>removeTrack(id)} className="text-white/40 hover:text-red-400 h-7 w-7"><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {availableTracks.length > 0 ? (
                <Sel data-testid="album-add-track-select" value="" onChange={(e)=>{ if(e.target.value) addTrack(e.target.value); }}>
                  <option value="">+ Add a track from your library…</option>
                  {availableTracks.map(t => <option key={t.id} value={t.id}>{t.title}{t.download_file ? "" : " (no MP3)"}</option>)}
                </Sel>
              ) : (
                <p className="text-white/25 text-xs text-center py-2">All tracks are in this album. Add more in the Music tab.</p>
              )}
            </div>

            {/* PRICING */}
            <div className="border-t border-white/5 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Bundle Price ($)"><Text type="number" step="0.01" min="0" value={form.price} onChange={(e)=>setField("price",parseFloat(e.target.value)||0)} disabled={form.is_free} /></Field>
                <Field label="Sale Price ($)" hint="(0 = no sale)"><Text type="number" step="0.01" min="0" value={form.sale_price} onChange={(e)=>setField("sale_price",parseFloat(e.target.value)||0)} disabled={form.is_free} /></Field>
                <div className="flex flex-col justify-end gap-2">
                  <div className="flex items-center gap-3"><Switch checked={form.is_free} onCheckedChange={(v)=>setField("is_free",v)} /><label className="text-sm text-white/60">Free Download</label></div>
                  <div className="flex items-center gap-3"><Switch checked={form.is_visible !== false} onCheckedChange={(v)=>setField("is_visible",v)} /><label className="text-sm text-white/60">Visible on Site</label></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 pt-4">
                <div className="flex items-center gap-3"><Switch checked={form.featured} onCheckedChange={(v)=>setField("featured",v)} /><label className="text-sm text-white/60">Featured</label></div>
                <div className="flex items-center gap-3"><Switch checked={form.exclusive} onCheckedChange={(v)=>setField("exclusive",v)} /><label className="text-sm text-white/60">Website Exclusive</label></div>
              </div>
            </div>

            {/* EXTRAS (Deluxe / Collector) */}
            <div className="border-t border-white/5 pt-3">
              <button type="button" onClick={()=>setShowExtras(!showExtras)} className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors">
                {showExtras ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Digital booklet + Deluxe/Collector extras
              </button>
              {showExtras && (
                <div className="space-y-4 mt-4">
                  <FileUpload label="Digital Booklet (PDF)" accept="application/pdf,.pdf" value={form.digital_booklet} onChange={(v)=>setField("digital_booklet",v)} />
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs uppercase tracking-wider text-white/40">Edition Extras <span className="text-white/20 normal-case tracking-normal">(stems, videos, notes — included in ZIP)</span></label>
                      <Button variant="ghost" size="sm" onClick={addExtra} className="text-[#D4AF37] hover:text-white h-7"><Plus className="w-3 h-3 mr-1" /> Add extra</Button>
                    </div>
                    {(form.edition_extras || []).length === 0 ? (
                      <p className="text-white/25 text-xs text-center py-2">No extras yet. Use for stems, behind-the-scenes clips, thank-you letters, etc.</p>
                    ) : (
                      <div className="space-y-3">
                        {form.edition_extras.map((ex, i) => (
                          <div key={i} className="bg-[#0A0A0A] border border-white/5 p-3 rounded space-y-2">
                            <div className="flex items-center gap-2">
                              <Text placeholder="Title (e.g. 'Stems Pack')" value={ex.title} onChange={(e)=>updateExtra(i,"title",e.target.value)} />
                              <Button variant="ghost" size="icon" onClick={()=>removeExtra(i)} className="text-white/40 hover:text-red-400 h-9 w-9 flex-shrink-0"><X className="w-4 h-4" /></Button>
                            </div>
                            <FileUpload accept="*/*" value={ex.file} onChange={(v)=>updateExtra(i,"file",v)} label="" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={()=>setOpen(false)} className="border-white/10 text-white/60">Cancel</Button>
              <Button data-testid="album-save-btn" onClick={save} className="bg-[#D4AF37] text-black hover:bg-white">{editId ? "Update" : "Add"} Release</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
